/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Tooltip, { TooltipPrimitive, type TooltipPrimitiveProps } from '@atlaskit/tooltip';

import type {
	InvitedAppShortcutAllowedApsKey,
	InvitedAppShortcutCollaborator,
} from '../../../../../common/types';
import { messages } from '../../../../../common/ui/invited-app-shortcuts/messages';
import { getAppKeyByApsKey } from '../../../../../common/utils/invited-app-shortcuts-utils';
import { useInvitedAppAnalytics } from '../../../../../services/invited-app-shortcuts/invited-app-analytics';

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		maxWidth: '300px',
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.small', '3px'),
		color: token('color.text.inverse'),
		font: token('font.body.small'),
		insetBlockStart: token('space.0'),
		insetInlineStart: token('space.0'),
		overflowWrap: 'break-word',
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.050'),
		paddingInlineEnd: token('space.075'),
		paddingInlineStart: token('space.075'),
		wordWrap: 'break-word',
	},
});

/**
 * As truncate is deprecated, we need to use a custom tooltip component to ensure the tooltip can show the message in one line.
 */
const CustomTooltip: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<TooltipPrimitiveProps> & React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(function CustomTooltip(
	{ children, className, ...rest },
	ref,
) {
	return (
		<TooltipPrimitive
			{...rest}
			// Manually passing on `className` so it gets merged correctly in the build output.
			// The passed classname is mostly used for integration testing (`.Tooltip`)
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides, @atlaskit/ui-styling-standard/no-classname-prop
			className={className}
			// "css" does not "exist" - it gets transformed into "className" by compiled
			css={styles.root}
			ref={ref}
			testId="invited-app-collaborators-tooltip"
		>
			{children}
		</TooltipPrimitive>
	);
});

interface InvitedAppCollaboratorsTooltipProps {
	children: React.ReactNode;
	currentAppCollaborators: InvitedAppShortcutCollaborator[];
	currentApsKey: InvitedAppShortcutAllowedApsKey;
}

export const InvitedAppCollaboratorsTooltip = ({
	children,
	currentApsKey,
	currentAppCollaborators,
}: InvitedAppCollaboratorsTooltipProps): JSX.Element => {
	const { fireViewedMoreCollaboratorsTooltip } = useInvitedAppAnalytics();
	const { formatMessage } = useIntl();

	const appMessage = useMemo(() => {
		switch (currentApsKey) {
			case 'JIRA_SOFTWARE':
				return messages.jira;
			case 'CONFLUENCE':
				return messages.confluence;
			// no default case as we only support JIRA_SOFTWARE and CONFLUENCE for now
		}
	}, [currentApsKey]);

	return (
		<Tooltip
			position="right"
			component={CustomTooltip}
			onShow={() =>
				fireViewedMoreCollaboratorsTooltip({
					app: getAppKeyByApsKey(currentApsKey),
					collaborators: currentAppCollaborators.length,
				})
			}
			content={
				<FormattedMessage
					{...messages.popupCollaboratorsTooltip}
					values={{
						numberOfCollaborators: currentAppCollaborators.length - 1,
						app: formatMessage(appMessage),
					}}
				/>
			}
		>
			{children}
		</Tooltip>
	);
};
